import { defineComponent as _defineComponent } from 'vue'
import {computed, inject, onMounted, reactive, ref, watch} from "vue";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import {NutrientType} from "@/models/Ingredient/NutrientType";
import {LabelConfigurationModel} from "@/models/NutritionLabels/v2/LabelConfigurationModel";
import RecipesRepository from "@/utils/API/RecipesRepository";
import {useNotify} from "@/hooks/useNotify";
import {LabelConfigurationOption} from "@/models/NutritionLabels/v2/enums/LabelConfigurationOption";
import {debounce} from "@/utils/Helpers";
import {useTranslations} from "@/hooks/useTranslations";
import {
  getCoreNutrients,
  getVoluntaryNutrients,
  OPTIONAL_CORE_SUPPLEMENT_NUTRIENTS,
  SUPPLEMENT_NUTRIENTS_CANADA,
  SUPPLEMENT_NUTRIENTS_US,
} from "@/models/NutritionLabels/v2/collections/NutrientGroups";
import {Blend} from "@/models/NutritionLabels/v2/Blend";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";

type UiRecipeRow = {
  rowId: string;
  recipeId: number | null;
  name: string;
  servingSizeGrams: number;
};

type RecipeOption = {
  id: number;
  name: string;
};

type FsisOptionKey =
  | "switchServingSizeText"
  | "showCalorieFromFatText"
  | "hideTotalText"
  | "shouldDisplayCalories449Text"
  | "switchCalorieBreakdownFootnote"
  | "useNormalServingSizeFont"
  | "showShortFooter"
  | "shouldDisplayNutrientsSideBySide"
  | "showSideBySidePanel"
  | "displayValueforCoreNutrients";


export default /*#__PURE__*/_defineComponent({
  __name: 'LabelConfiguration',
  props: {
    model: null,
    requestedConfigurationOptions: null,
    country: null,
    shouldExpand: { type: Boolean },
    stepLabel: { default: "2" },
    roundingRuleIndex: { default: 0 },
    labelType: null
  },
  emits: ["update:model", "refreshLabel"],
  setup(__props: any, { emit }) {

const props = __props as { model: LabelConfigurationModel, requestedConfigurationOptions: LabelConfigurationOption[], country: NutritionLabelCountry, shouldExpand: boolean, stepLabel: string, roundingRuleIndex: number, labelType?: string };



const MAX_RECIPES_AGGREGATE_LABEL = 5;
const uiRecipes = ref<UiRecipeRow[]>([]);

const fetchPreview: () => void = inject("bopFetchPreview", () => {});

// Local copy of the model
const localModel = reactive<LabelConfigurationModel>(
  JSON.parse(JSON.stringify(props.model))
);

const notify = useNotify();
const translations = useTranslations();
const recipeOptions = ref<RecipeOption[]>([]);
const recipeOptionsLoading = ref(false);
const recipeOptionsLoadFailed = ref(false);
const isFsisRoundingRule = computed(() => [1, 2].includes(props.roundingRuleIndex));
const fsisOptionKeys: FsisOptionKey[] = [
  "switchServingSizeText",
  "showCalorieFromFatText",
  "hideTotalText",
  "shouldDisplayCalories449Text",
  "switchCalorieBreakdownFootnote",
  "useNormalServingSizeFont",
  "showShortFooter",
  "shouldDisplayNutrientsSideBySide",
  "showSideBySidePanel",
  "displayValueforCoreNutrients",
];

const coreNutrientsByCountry = computed(() => {
  switch (props.country) {
  case NutritionLabelCountry.US:
    if (props.requestedConfigurationOptions.includes(LabelConfigurationOption.SUPPLEMENT_NUTRIENTS)) {
      return OPTIONAL_CORE_SUPPLEMENT_NUTRIENTS;
    }
    return getCoreNutrients(props.country, props.roundingRuleIndex);

  case NutritionLabelCountry.Canada:
    return getCoreNutrients(props.country, props.roundingRuleIndex);

  default:
    return [];
  }
});

const hasTwoRecipeRequirement = computed(() => props.requestedConfigurationOptions.includes(
  LabelConfigurationOption.RECIPES_AND_UNIT_TWO
));

const isSupplementTwoRecipeLabel = computed(() => hasTwoRecipeRequirement.value
  && props.requestedConfigurationOptions.includes(LabelConfigurationOption.SUPPLEMENT_NUTRIENTS));

const recipesSectionTitle = computed(() => isSupplementTwoRecipeLabel.value
  ? "Packets to Display"
  : "Recipes to Display");

const missingSecondRecipeMessage = computed(() => isSupplementTwoRecipeLabel.value
  ? "Please add a PM Packet recipe."
  : "Please add a second recipe for the dual-column label.");

const cardStatus = computed(() => {
  const dualColumnSelected = hasTwoRecipeRequirement.value;

  if (!dualColumnSelected) {
    return "default";
  }

  const selectedRecipes = uiRecipes.value.filter((r) => r.recipeId !== null);

  return selectedRecipes.length === 2 ? "default" : "error";
});

const supplementNutrientsByCountry = computed(() => {
  switch (props.country) {
  case NutritionLabelCountry.Canada:
    return SUPPLEMENT_NUTRIENTS_CANADA;

  case NutritionLabelCountry.US:
    if(props.requestedConfigurationOptions.includes(LabelConfigurationOption.SUPPLEMENT_NUTRIENTS)) {
      return SUPPLEMENT_NUTRIENTS_US;
    }
    return getCoreNutrients(props.country, props.roundingRuleIndex);

  default:
    return [];
  }
});

watch(
  () => props.model,
  (val) => {
    Object.assign(localModel, val);
    const didResetNonFsisOptions = resetFsisOptionsIfNotFsis();
    syncRecipesFromModel();

    if (didResetNonFsisOptions) {
      emitUpdate();
    }
  },
  { immediate: true }
);

watch(
  isFsisRoundingRule,
  (isFsis) => {
    if (!isFsis && resetFsisOptionsIfNotFsis()) {
      emitUpdate();
    }
  },
  { immediate: true }
);

watch(
  () => recipeOptions.value,
  () => {
    if (!recipeOptions.value.length) return;
    syncRecipesFromModel();
  }
);

watch(
  () => props.country,
  () => {
    sanitizeSelectedNutrients();
    emitUpdate();
  }
);

watch(
  () => props.roundingRuleIndex,
  () => {
    sanitizeSelectedNutrients();
    emitUpdate();
  }
);

watch(
  () => props.requestedConfigurationOptions,
  (newVal) => {
    const hasAnyQuantity = newVal.includes(LabelConfigurationOption.RECIPES_AND_UNIT_ANY_QUANTITY);
    const hasTwoOnly = newVal.includes(LabelConfigurationOption.RECIPES_AND_UNIT_TWO);

    const max = hasTwoOnly ? 2 : hasAnyQuantity ? MAX_RECIPES_AGGREGATE_LABEL : 1;

    if (uiRecipes.value.length > max) {
      uiRecipes.value.splice(max);
      syncModelRecipesFromUi();
      emitUpdate();
    }

    if (uiRecipes.value.length === 0 && (hasAnyQuantity || hasTwoOnly) && !props.model.recipes?.length) {
      uiRecipes.value.push({
        rowId: crypto.randomUUID(),
        recipeId: null,
        name: "",
        servingSizeGrams: 100
      });
      syncModelRecipesFromUi();
      emitUpdate();
    }
  },
  { immediate: true }
);

onMounted(() => {
  fetchRecipeListing();
});

function fetchRecipeListing() {
  recipeOptionsLoading.value = true;
  recipeOptionsLoadFailed.value = false;

  RecipesRepository.listRecipeSelections()
    .then((response) => {
      recipeOptions.value = Array.isArray(response.data) ? response.data : [];
    })
    .catch((error) => {
      const message = error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
        || error?.response?.data?.message
        || translations.value.get("network-error");
      recipeOptionsLoadFailed.value = true;
      notify.error(message);
    })
    .finally(() => {
      recipeOptionsLoading.value = false;
    });
}

function syncRecipesFromModel() {
  if (!recipeOptions.value.length) {
    return;
  }

  uiRecipes.value = props.model.recipes.map((r) => {
    const match = recipeOptions.value.find((opt) => opt.id === r.recipeId);

    return {
      rowId: crypto.randomUUID(),
      recipeId: r.recipeId,
      name: match?.name ?? "Unknown recipe",
      servingSizeGrams: Number(r.servingSizeGrams) || 100
    };
  });
}

function updateRecipe(
  row: UiRecipeRow,
  selected: RecipeOption | null
) {
  if (!selected) {
    row.recipeId = null;
    row.name = "";
  }
  else {
    row.recipeId = selected.id;
    row.name = selected.name;
  }

  syncModelRecipesFromUi();
  emitUpdate();
}

function emitUpdate() {
  resetFsisOptionsIfNotFsis();
  sanitizeSelectedNutrients();
  emit("update:model", localModel);
  debouncedFetchPreview();
}

function resetFsisOptionsIfNotFsis() {
  if (isFsisRoundingRule.value) {
    return false;
  }

  let updated = false;
  const modelWithFsisFlags = localModel as LabelConfigurationModel & Record<FsisOptionKey, boolean>;

  for (const key of fsisOptionKeys) {
    if (modelWithFsisFlags[key]) {
      modelWithFsisFlags[key] = false;
      updated = true;
    }
  }

  return updated;
}

function sanitizeSelectedNutrients() {
  const voluntaryNutrientsByCountry = getVoluntaryNutrients(props.country, props.roundingRuleIndex, props.labelType);

  localModel.selectedCoreNutrients = localModel.selectedCoreNutrients.filter((n) =>
    coreNutrientsByCountry.value.includes(n)
  );

  localModel.selectedSupplementNutrients = localModel.selectedSupplementNutrients.filter((n) =>
    supplementNutrientsByCountry.value.includes(n)
  );

  localModel.selectedVoluntaryNutrients = localModel.selectedVoluntaryNutrients.filter((n) =>
    voluntaryNutrientsByCountry.includes(n)
  );
}

function syncModelRecipesFromUi() {
  localModel.recipes = uiRecipes.value.map((r) => ({
    recipeId: r.recipeId,
    servingSizeGrams: String(r.servingSizeGrams)
  }));
}

function getRecipeLabel(index: number) {
  if (!isSupplementTwoRecipeLabel.value) {
    return "Recipe Name";
  }

  return index === 0 ? "AM Packet Recipe" : "PM Packet Recipe";
}

function getServingSizeLabel(index: number) {
  if (!isSupplementTwoRecipeLabel.value) {
    return "Serving size (grams)";
  }

  return index === 0 ? "AM Packet Serving size (grams)" : "PM Packet Serving size (grams)";
}

function getRecipeRowValidationMessage(index: number) {
  if (!isSupplementTwoRecipeLabel.value) {
    return "Recipe and serving size must be defined.";
  }

  return index === 0
    ? "AM Packet recipe and serving size must be defined."
    : "PM Packet recipe and serving size must be defined.";
}

function debouncedFetchPreview() {
  debounce(fetchPreview, 1000, "back-of-package-config-label-update");
}

function addBlendRow() {
  localModel.proprietaryBlends.push({ name: "", value: "", dailyValue: "", subBlends: [] });
  emitUpdate();
}

function removeBlendRow(index: number) {
  localModel.proprietaryBlends.splice(index, 1);
  emitUpdate();
}

function addSubBlendRow(blend: Blend) {
  blend.subBlends.push({
    unit: "",
    name: "",
    value: "",
    dailyValue: ""
  });
  emitUpdate();
}

function removeSubBlendRow(blend: Blend, index: number) {
  blend.subBlends.splice(index, 1);
  emitUpdate();
}

function shouldAllowAddRecipe() {
  const isTwoRecipeOnly = props.requestedConfigurationOptions.includes(LabelConfigurationOption.RECIPES_AND_UNIT_TWO);
  const maxRecipes = isTwoRecipeOnly ? 2 : MAX_RECIPES_AGGREGATE_LABEL; // temp max value for "arbitrary" mode

  return uiRecipes.value.length < maxRecipes;
}

function onServingSizeChange() {
  syncModelRecipesFromUi();
  emitUpdate();
}

function addRecipeRow() {
  if (!shouldAllowAddRecipe()) return;

  uiRecipes.value.push({
    rowId: crypto.randomUUID(),
    recipeId: null,
    name: "",
    servingSizeGrams: 100
  });

  syncModelRecipesFromUi();
  emitUpdate();
}

function removeRecipeRow(index: number) {
  uiRecipes.value.splice(index, 1);

  syncModelRecipesFromUi();
  emitUpdate();
}

return { __sfc: true,props, MAX_RECIPES_AGGREGATE_LABEL, uiRecipes, emit, fetchPreview, localModel, notify, translations, recipeOptions, recipeOptionsLoading, recipeOptionsLoadFailed, isFsisRoundingRule, fsisOptionKeys, coreNutrientsByCountry, hasTwoRecipeRequirement, isSupplementTwoRecipeLabel, recipesSectionTitle, missingSecondRecipeMessage, cardStatus, supplementNutrientsByCountry, fetchRecipeListing, syncRecipesFromModel, updateRecipe, emitUpdate, resetFsisOptionsIfNotFsis, sanitizeSelectedNutrients, syncModelRecipesFromUi, getRecipeLabel, getServingSizeLabel, getRecipeRowValidationMessage, debouncedFetchPreview, addBlendRow, removeBlendRow, addSubBlendRow, removeSubBlendRow, shouldAllowAddRecipe, onServingSizeChange, addRecipeRow, removeRecipeRow, CollapsableCard, NutrientType, LabelConfigurationOption, getVoluntaryNutrients }
}

})